import type { OpenStoreButtonLocation, OpenStoreTriggerBy, PurchaseSource } from '@sunflower-games/analytics-events';
import axios, { AxiosError } from 'axios';
import dayjs from 'dayjs';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { action, autorun, computed, makeObservable, observable } from 'mobx';

import { formatRedemptionLimitMessage } from '../../components/FullPageDialogs/RedeemDialog/helpers';
import {
  type IHandleConfirmRedeemData,
  type IInitRedeem,
  type IRedeemData,
  type WithdrawBalanceType,
} from '../../components/FullPageDialogs/RedeemDialog/types';
import {
  CURRENT_PAYMENT_APP_VERSION,
  SOCKET_ACCOUNT_CAPTURE,
  SOCKET_USER_TRANSACTION,
  SOCKET_USER_TRANSACTION_REDEEM,
} from '../../config/constants';
import { territoriesApi } from '../../features/geography/api/territories';
import { NOT_SUPPORTED, RDM_METHOD_BY_STORED_METHOD } from '../../features/payments/banking-method-mappings';
import { RedemptionProvider } from '../../features/payments/redemptions/enums';
import type { InitGenericRedemptionResult } from '../../features/payments/redemptions/types';
import { getHasSuccessfulPurchaseWithPaymentMethod } from '../../Payments/v2/src/api';
import { analytics } from '../../services/analytics';
import { deleteRequest, getRequest, patchRequest, postRequest } from '../../services/api';
import { logToNewRelic } from '../../services/debug';
import { getUserStats, resolveFlag } from '../../services/launchDarkly';
import { isPurchaseLimitError } from '../../services/payments';
import { pendingTransactionsRegistry } from '../../services/pendingTransactionsRegistry';
import { platformContextService } from '../../services/platformContext/PlatformContextService';
import { socket } from '../../services/socket';
import soundEffects from '../../services/sounds/soundEffects';
import type { ApiResponse, IUserDetailsData } from '../../services/types';
import { isProduction } from '../../utils/getEnvironment';
import { generateGuid } from '../../utils/helpers';
import { loadScript } from '../../utils/loadScript';
import { devError } from '../../utils/log';
import { singleFlight } from '../../utils/singleFlight';
import { skrillMakePurchase } from '../../utils/skrillMakePurchase';
import { type IOrderPackage, SystemErrorCodes } from '../app/types';
import type { FeatureFetchStatus, IFeatureManagerStore } from '../feature-manager/types';
import { type RootStore } from '../RootStore';
import type { ClaimableReward } from '../ui/types';
import { EnumOpenDialog } from '../ui/types';

import { paysafeCardErrorMessages, paysafeErrorMapper } from './constants';
import {
  type ApiRedemptionCaptureResult,
  type CvvErrorListener,
  EnumPaymentMethods,
  type IAccountCaptureResponseCbArgs,
  type IBankAccountRedeem,
  type IBillingAddress,
  type ICheckForceKYC,
  type IConfirmRedeemResponse,
  type IConfirmRedeemSafeChargeData,
  type IInitPaysafeCreditCardInstance,
  type IInitPaysafeInstance,
  type IInitSafeChargeInstance,
  type ILastPaymentMethod,
  type INewCardBillingDetails,
  type IPackageData,
  type IPaymentAuthorizeRequest,
  type IPayoutsRedeemRequest,
  type IPreviousPaymentData,
  type IRedeemFailure,
  type IRedeemTransaction,
  type ISafeChargePaymentMethod,
  type ISavedCreditCardData,
  type ISendConfirmRedeemSafeChargeData,
  type IUpoData,
  type IUpoDataCardRedeem,
  type IValidateMerchantRequest,
  RedeemTransactionStatus,
  type RedemptionCaptureResult,
  TransactionStatus,
  TSafeChargeMethodType,
  TSafeChargePaymentType,
  type UcvStatus,
  type UPODeletionResult,
  type UserTransaction,
} from './types';
import { MultiplyBy100AndRound } from './utils';

import ApplePayPaymentRequest = ApplePayJS.ApplePayPaymentRequest;

const { VITE_PAYSAFE_API_KEY, VITE_PAYSAFE_ACCOUNT_FMA, VITE_PAYSAFE_ENVIROMENT } = import.meta.env;

const InitCheckForceKYC = {
  isForceKYC: false,
  isProcessForKYC: false,
  isSubscription: false,
};

export class PayRedeemStore implements IFeatureManagerStore<'RedemptionApproval'> {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    const subscription = this.subscribeToSocket.bind(this);
    const subscribeToSocketBankAccount = this.subscribeToSocketBankAccount.bind(this);
    const subscribeToRedeemTransaction = this.subscribeToRedeemTransaction.bind(this);
    autorun(() => {
      if (this.rootStore.authStore.authorized) {
        socket.on(SOCKET_USER_TRANSACTION, subscription);
        socket.on(SOCKET_ACCOUNT_CAPTURE, subscribeToSocketBankAccount);
        socket.on(SOCKET_USER_TRANSACTION_REDEEM, subscribeToRedeemTransaction);
      } else {
        socket.off(SOCKET_USER_TRANSACTION, subscription);
        socket.off(SOCKET_ACCOUNT_CAPTURE, subscribeToSocketBankAccount);
        socket.off(SOCKET_USER_TRANSACTION_REDEEM, subscribeToRedeemTransaction);
        this.setRedemptionCaptureResult(null);
      }
    });

    this.initPaymentInstance();
  }

  @observable public isPaymentLoading: boolean = false;
  @observable public isPaymentProcess: boolean = false;

  @observable public showPreviousPayment: boolean = false;

  @observable public paysafeInstance: IInitPaysafeInstance | undefined = undefined;
  @observable public paysafeApplePayInstance: IInitPaysafeInstance | undefined = undefined;
  @observable public paysafeCreditCardInstance: IInitPaysafeCreditCardInstance | undefined = undefined;
  @observable public safeChargeInstance: IInitSafeChargeInstance | undefined = undefined;
  @observable public safeChargeRedeemInstance: IInitSafeChargeInstance | undefined = undefined;

  @observable public redeemCoins: number = 0;
  @observable public isRedeem: boolean = false;
  @observable public redeemSafeChargeSuccessTransactionId: number | null = null;
  @observable public confirmRedeemSafeChargeData: IConfirmRedeemSafeChargeData | null = null;
  @observable public upoData: IUpoData = {
    card: undefined,
    bankAccount: undefined,
  };
  @observable public cardRedeemUpo: IUpoDataCardRedeem[] = [];
  @observable public cardRedeemUpoInternal: IUpoDataCardRedeem[] = [];
  @observable public processRedeem: boolean = false;
  @observable public safeChargePaymentMethods: ISafeChargePaymentMethod[] = [];
  @observable public paymentMethod: EnumPaymentMethods | null = null;

  @observable public amountRedeem: number = 0;
  @observable public isRedeemSuccess: boolean = false;
  @observable public redeemFailure: IRedeemFailure = { isFailed: false };
  @observable public isRedeemKYCFailed: boolean = false;
  @observable public redeemData: IRedeemData | null = null;
  @observable public previousPaymentData: IPreviousPaymentData | null = null;
  @observable public lastPaymentData: ILastPaymentMethod | null = null;
  @observable public redeemTransactions: {
    SC: IRedeemTransaction[];
    CASH: IRedeemTransaction[];
    CROWN_TICKETS: IRedeemTransaction[];
  } = {
    SC: [],
    CASH: [],
    CROWN_TICKETS: [],
  };
  @observable public newCardBillingDetails: INewCardBillingDetails | null = null;
  @observable public checkForceKYC: ICheckForceKYC = InitCheckForceKYC;
  @observable public safeChargeCard: any = null;

  @observable public redemptionCaptureResult: RedemptionCaptureResult | null = null;
  @observable public redemptionFlowInProgress: boolean = false;

  @observable public hasPaysafecardPurchaseHistoryForRedeem: boolean = false;
  @observable public bankAccounts: IBankAccountRedeem | null = null;
  @observable public isDeletingBankAccount: boolean = false;

  @observable public afterPurchaseFlowInProgress: boolean = false;
  @observable public afterSuccessfulPurchaseCallback: VoidFunction | null = null;

  // Bumped when a purchase completes while a game-ui game is open (the success dialog is skipped) —
  // GameUiDialog watches it and pushes a wallet refresh into the game iframe.
  @observable public gameUiPurchaseSuccessCount: number = 0;
  /** Bumped when the payments app reports a completed Crown Rips shipment (flow/ship-success) —
   *  GameUiDialog relays it into the game as `shipCardComplete` so the vault refreshes. */
  @observable public gameUiShipSuccessCount: number = 0;

  @computed
  public get withdrawBalanceType(): WithdrawBalanceType {
    // Bingo redeem keeps its own 'CASH' flow (untouched). Indiana cash-winnings (wsc) is a SEPARATE
    // flow 'CROWN_TICKETS' that reuses the SC redeem design — never the bingo modal. `wsc_available`
    // is resolved server-side and returned on the profile (`?? true` = off until loaded).
    const cashWinningsMode = !(this.rootStore.userStore.profile?.geoRestrictionMetadata?.isWscAvailable ?? true);
    if (this.rootStore.bingoStore.isInsideBingoArea) {
      return 'CASH';
    }
    return cashWinningsMode ? 'CROWN_TICKETS' : 'SC';
  }

  // The server only knows 'SC' / 'CASH'. CROWN_TICKETS is a client-only wsc flow that redeems cash,
  // so it maps to 'CASH' on every request; the client keeps its own CROWN_TICKETS bucket/state.
  private toApiWithdrawBalanceType(type: WithdrawBalanceType): 'SC' | 'CASH' {
    return type === 'CROWN_TICKETS' ? 'CASH' : type;
  }

  // Coin type for redeem-transactions (fetch + bucket + read). wsc (CROWN_TICKETS) shares the cash
  // history, so it maps to CASH — keeps fetch/bucket/read aligned so history/pending populate.
  @computed
  public get redeemTransactionsCoinType(): 'SC' | 'CASH' {
    return this.toApiWithdrawBalanceType(this.withdrawBalanceType);
  }

  // The payment platform, not the page's: `WEB` inside the mobile shell, like the payments/redeem iframe.
  private get platform() {
    return platformContextService.getPaymentPlatform();
  }

  @action
  public setAfterPurchaseFlowInProgress(inProgress: boolean) {
    this.afterPurchaseFlowInProgress = inProgress;
  }

  @action
  private bumpGameUiPurchaseSuccess() {
    this.gameUiPurchaseSuccessCount += 1;
  }

  @action
  public bumpGameUiShipSuccess() {
    this.gameUiShipSuccessCount += 1;
  }

  @action
  public setRedemptionFlowInProgress(inProgress: boolean) {
    this.redemptionFlowInProgress = inProgress;
  }

  @action
  public setHasPaysafecardPurchaseHistoryForRedeem(value: boolean) {
    this.hasPaysafecardPurchaseHistoryForRedeem = value;
  }

  @action
  public async prefetchPaysafecardPurchaseHistoryForRedeem(): Promise<void> {
    try {
      const payload = await getHasSuccessfulPurchaseWithPaymentMethod(RedemptionProvider.PAYSAFECARD);
      this.setHasPaysafecardPurchaseHistoryForRedeem(payload.hasSuccessfulPurchase);
    } catch {
      this.setHasPaysafecardPurchaseHistoryForRedeem(false);
    }
  }

  @action
  public setRedemptionBankAccounts(bankAccounts: any) {
    this.bankAccounts = bankAccounts;
  }

  @action
  private setIsDeletingBankAccount(value: boolean) {
    this.isDeletingBankAccount = value;
  }

  public transactionsEventsRegistry = new Map<
    string,
    {
      transactionId: string;
      amount?: number;
      isFirstDeposit?: boolean;
      selectedPackage?: IOrderPackage;
      isHandled?: boolean;
      paymentMethod?: EnumPaymentMethods;
      actualGrantedRewards?: ClaimableReward;
    }
  >([]);

  public inProcessTransactionId = '';
  public setInProcessTransactionId(transactionId: string) {
    this.inProcessTransactionId = transactionId;
  }

  @computed
  public get redeemTransactionsByCurrentType() {
    return this.redeemTransactions[this.redeemTransactionsCoinType];
  }

  @computed
  public get pendingRedeemScTransactions(): IRedeemTransaction[] {
    return this.redeemTransactions['SC'].filter((t) => t.status === RedeemTransactionStatus.PENDING);
  }

  @computed
  public get pendingRedeemCashTransactions(): IRedeemTransaction[] {
    return this.redeemTransactions['CASH'].filter((t) => t.status === RedeemTransactionStatus.PENDING);
  }

  // Indiana (cash-winnings): the general redeem lobby lists SC + cash redemptions together. SC and CASH
  // buckets are disjoint by coinType; dedupe by id for safety, then sort by createdAt desc (newest first).
  @computed
  public get allRedeemTransactions(): IRedeemTransaction[] {
    const byId = new Map<number, IRedeemTransaction>();
    for (const tx of [...this.redeemTransactions.SC, ...this.redeemTransactions.CASH]) {
      byId.set(tx.withdrawRequestId, tx);
    }
    return [...byId.values()].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  @computed
  public get allPendingRedeemTransactions(): IRedeemTransaction[] {
    return this.allRedeemTransactions.filter((tx) => tx.status === RedeemTransactionStatus.PENDING);
  }

  @computed
  public get tokenizeWithSavedCard() {
    return this.previousPaymentData?.method === EnumPaymentMethods.card;
  }

  @action
  public setPaymentMethod(paymentMethod: EnumPaymentMethods | null) {
    this.paymentMethod = paymentMethod;
  }

  @action
  public setSafeChargeCard(card: any) {
    this.safeChargeCard = card;
  }

  @action
  public setRedeemCoins(coins: number) {
    this.redeemCoins = coins;
  }

  @action
  public setIsRedeem(isRedeem: boolean) {
    this.isRedeem = isRedeem;
  }

  @action
  public setShowPreviousPayment(showPreviousPayment: boolean) {
    this.showPreviousPayment = showPreviousPayment;
  }

  @action
  public setRedeemSafeChargeSuccessTransactionId(data: number | null) {
    this.redeemSafeChargeSuccessTransactionId = data;
  }

  @action
  public setConfirmRedeemSafeChargeData(data: IConfirmRedeemSafeChargeData | null) {
    this.confirmRedeemSafeChargeData = data;
  }

  @action
  public setPaysafeApplePayInstance(instance: IInitPaysafeInstance | undefined) {
    this.paysafeApplePayInstance = instance;
  }

  @action
  public setPaysafeCreditCardInstance(instance: IInitPaysafeCreditCardInstance | undefined) {
    this.paysafeCreditCardInstance = instance;
  }

  @action
  public setPaymentLoading(isLoading: boolean) {
    this.isPaymentLoading = isLoading;
  }

  @action
  public setPaymentProcess(isProcess: boolean) {
    this.isPaymentProcess = isProcess;
  }

  @action
  public setAfterSuccessfulPurchaseCallback(callback: VoidFunction | null) {
    this.afterSuccessfulPurchaseCallback = callback;
  }

  @action
  public initRedeemSafeChargeInstance() {
    void loadScript('https://cdn.safecharge.com/safecharge_resources/v1/websdk/safecharge.js').then(async () => {
      const response = await postRequest<{
        data: {
          openOrder: {
            sessionToken: string;
          };
        };
      }>('user/open-order', { paymentType: TSafeChargePaymentType.redeem, coins: this.redeemCoins });

      const sessionToken = get(response, 'data.openOrder.sessionToken', null);
      const safeCharge = window.SafeCharge({
        env: isProduction ? 'prod' : 'int',
        merchantId: import.meta.env.VITE_MERCHANT_ID,
        merchantSiteId: import.meta.env.VITE_MERCHANT_SITE_ID,
        sessionToken: sessionToken,
      });

      this.safeChargeRedeemInstance = safeCharge;
    });
  }

  @action
  public initSafeChargeInstance() {
    void loadScript('https://cdn.safecharge.com/safecharge_resources/v1/websdk/safecharge.js').then(() => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const safeCharge = window.SafeCharge({
        env: isProduction ? 'prod' : 'int',
        merchantId: import.meta.env.VITE_MERCHANT_ID,
        merchantSiteId: import.meta.env.VITE_MERCHANT_SITE_ID,
      });
      this.safeChargeInstance = safeCharge;
    });
  }

  @action
  public initPaysafeInstance() {
    void loadScript('https://hosted.paysafe.com/js/v1/latest/paysafe.min.js').then(async () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const instance = await window.paysafe;

      this.paysafeInstance = instance;
    });
  }

  @action
  public initPaymentInstance() {
    //TODO: add a handler that will determine which instance needs to be initialized now
    if (!this.paysafeInstance) {
      this.initPaysafeInstance();
    }

    if (!this.safeChargeInstance) {
      this.initSafeChargeInstance();
    }
  }

  public openMiniStore({
    triggeredBy,
    eventAction,
    clickId,
    buttonLocation,
  }: {
    eventAction: 'impression' | 'click';
    triggeredBy: OpenStoreTriggerBy;
    clickId?: string;
    buttonLocation?: OpenStoreButtonLocation;
  }) {
    if (this.rootStore.uiStore.isVisibleMiniStore) {
      return;
    }

    if (eventAction === 'click' && triggeredBy === 'click' && clickId && buttonLocation) {
      void analytics.sendEvent('purchase', {
        step_name: 'store',
        event_action: 'click',
        triggered_by: triggeredBy,
        store_type: 'mini-store',
        button_location: buttonLocation,
        store_segment: this.rootStore.loyaltyProgramStore.currentTierKey,
        click_id: clickId,
        store_key: this.rootStore.appStore.currentStore?.key || '',
        sign_up_date: this.rootStore.userStore.profile?.createdAt || '',
      });
    }

    if (eventAction === 'impression') {
      void analytics.sendEvent('purchase', {
        step_name: 'store',
        event_action: eventAction,
        triggered_by: triggeredBy,
        store_type: 'mini-store',
        store_segment: this.rootStore.loyaltyProgramStore.currentTierKey,
        store_offers_benefit_ids: [],
        store_key: this.rootStore.appStore.currentStore?.key || '',
        sign_up_date: this.rootStore.userStore.profile?.createdAt || '',
      });
    }

    const preventPurchase = resolveFlag('prevent_purchase', false);

    if (preventPurchase) {
      this.rootStore.uiStore.setOpenFullPageDialog('prevent_purchase');
    } else {
      this.rootStore.uiStore.setIsVisibleMiniStore(true);
    }

    soundEffects.playSound('popup-open');
  }

  public closeMiniStore() {
    this.rootStore.uiStore.setIsVisibleMiniStore(false);
    soundEffects.playSound('popup-close');
  }

  public toggleMiniStore(params: {
    eventAction: 'impression' | 'click';
    triggeredBy: OpenStoreTriggerBy;
    clickId?: string;
    buttonLocation?: OpenStoreButtonLocation;
  }) {
    if (!this.rootStore.uiStore.isVisibleMiniStore) {
      this.openMiniStore(params);
    } else {
      this.closeMiniStore();
    }
  }

  private getPaysafeErrorDetails(error: any, errCode?: number) {
    const details = error.response?.data?.errors[0]?.message || error.detailedMessage;
    const errorCode = errCode || error.fields?.errorCode || error.code;

    const mildFailuresDesignFF = resolveFlag('mild_failures_design', true);
    const errorMapper = mildFailuresDesignFF ? paysafeErrorMapper : paysafeCardErrorMessages;
    const errDetails = errorMapper[errorCode];

    return errDetails || details;
  }

  public handleBuyPaysafeApplePay(onPurchaseLimitExceeded?: VoidFunction) {
    if (this.paysafeApplePayInstance) {
      // TransactionId and merchantRefNum are the same entity
      const transactionId = generateGuid();
      const profile = this.rootStore.userStore.profile;
      const amount = MultiplyBy100AndRound(this.rootStore.appStore.selectedPackage?.price);

      const billingDetails = {
        country: profile?.cleanGeoData?.countryCode || '',
        zip: profile?.zipCode || '',
        street: profile?.address || '',
        city: profile?.city || '',
        state: profile?.cleanGeoData?.territoryCode || '',
      };

      const testOptionTokenize = {
        amount,
        transactionType: 'PAYMENT',
        paymentType: EnumPaymentMethods.applePay,
        applePay: {
          country: 'US',
        },
        customerDetails: {
          billingDetails,
          profile: {
            firstName: profile?.firstName,
            lastName: profile?.lastName,
            email: profile?.email,
          },
        },
        merchantRefNum: transactionId,
      };

      const packageId = this.rootStore.appStore.selectedPackage?.packageId;

      this.paysafeApplePayInstance
        .tokenize(testOptionTokenize)
        .then(async (result) => {
          const data = {
            packageId,
            paymentMethod: EnumPaymentMethods.applePay,
            merchantRefNum: transactionId,
            paymentHandleToken: result.token,
            platform: this.platform,
            ...this.rootStore.appStore.currentOrderPackage?.eventTriggerData,
          };

          const response = await postRequest<
            ApiResponse<{
              success: boolean;
              details: {
                transactionId: string;
                amount: number;
                isFirstDeposit: boolean;
              };
            }>
          >('user/proceed-payment-paysafe', data);

          logToNewRelic(
            'pre sendSuccessPurchaseEvent',
            {
              message: 'in handleBuyPaysafeApplePay',
              data: response.data,
            },
            'info',
          );

          if (response.data.success) {
            this.paysafeApplePayInstance?.complete('success');
            this.setInProcessTransactionId(transactionId);
            this.transactionsEventsRegistry.set(transactionId, {
              amount,
              isFirstDeposit: response.data.data?.details?.isFirstDeposit,
              transactionId: response.data.data?.details?.transactionId || transactionId,
            });
            this.handleSuccessPurchase();
          } else {
            this.rootStore.uiStore.setOpenDialog({
              dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
              options: { type: 'purchase' },
            });
          }
        })
        .catch((error) => {
          logToNewRelic('Paysafe Apple Pay error', JSON.stringify(error), 'error');
          if (error?.code === '9152') return;

          if (typeof onPurchaseLimitExceeded === 'function' && isPurchaseLimitError(error)) {
            return onPurchaseLimitExceeded();
          }

          const errorName = error.response?.data?.errors[0]?.name || error.name;

          if (errorName === 'ForceKYCError') {
            this.rootStore.uiStore.setOpenDialog({
              dialog: EnumOpenDialog.isOpenKycDialog,
            });
            return;
          }

          this.paysafeApplePayInstance?.complete('fail');
          this.rootStore.uiStore.setOpenDialog({
            dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
            options: { type: 'purchase' },
          });
        });
    } else {
      this.rootStore.uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
        options: { type: 'purchase' },
      });
    }
  }

  public handleBuyApplePay(onPurchaseLimitExceeded?: VoidFunction) {
    const selectedPackage = this.rootStore.appStore.selectedPackage;
    const profile = this.rootStore.userStore.profile;

    if (!profile?.cleanGeoData?.countryCode) {
      console.error('Attempt to perform Apple payment without geolocation data on the profile');

      return;
    }

    const billingAddress: IBillingAddress = {
      territoryKey: profile?.cleanGeoData?.territoryKey || '',
      zip: profile?.zipCode || '',
      street: profile?.address || '',
      city: profile?.city || '',
    };
    const packageData: IPackageData = {
      packageId: selectedPackage?.packageId,
    };
    const applePayVersion = 10;
    const paymentRequest: ApplePayPaymentRequest = {
      currencyCode: 'USD',
      countryCode: profile?.cleanGeoData?.countryCode,
      merchantCapabilities: ['supports3DS'],
      supportedNetworks: ['Visa', 'MasterCard'],
      total: {
        label: 'Crown Coins Casino',
        amount: selectedPackage ? `${selectedPackage.price}` : '',
      },
    };
    const applePaySession: ApplePaySession = new ApplePaySession(applePayVersion, paymentRequest);
    applePaySession.onvalidatemerchant = (e) => void this.onValidateMerchant(e, applePaySession);
    applePaySession.onpaymentauthorized = (e) =>
      void this.onPaymentAuthorized(e, applePaySession, billingAddress, packageData, onPurchaseLimitExceeded);
    applePaySession.begin();
  }

  private async onValidateMerchant(
    event: ApplePayJS.ApplePayValidateMerchantEvent,
    session: ApplePaySession,
    onPurchaseLimitExceeded?: VoidFunction,
  ) {
    try {
      const requestBody: IValidateMerchantRequest = { validationURL: event.validationURL };
      const resp = await postRequest<ApiResponse<any, 'details'>>('payment/verify-merchant-apple-pay', requestBody);
      session.completeMerchantValidation(resp.data.details);
    } catch (e) {
      session.abort();
      this.handleApplePayError(e, onPurchaseLimitExceeded);
    }
  }

  private async onPaymentAuthorized(
    event: ApplePayPaymentAuthorizedEvent,
    session: ApplePaySession,
    billingAddress: IBillingAddress,
    packageData: IPackageData,
    onPurchaseLimitExceeded?: VoidFunction,
  ) {
    try {
      const requestBody: IPaymentAuthorizeRequest = {
        payload: {
          applePay: event.payment,
        },
        billingAddress,
        packageData,
        platform: this.platform,
        ...this.rootStore.appStore.currentOrderPackage?.eventTriggerData,
      };
      const { uiStore } = this.rootStore;
      const resp = await postRequest('payment/purchase-apple-pay', requestBody);
      if (resp.data.success) {
        session.completePayment(ApplePaySession.STATUS_SUCCESS);
        // This path opens the success dialog directly rather than going through
        // handleSuccessPurchase, so the slot refresh signal has to be sent here too.
        this.rootStore.gameStore.refreshSlotBalance();
        uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenPurchaseSuccessDialog });
      }
    } catch (e) {
      session.completePayment(ApplePaySession.STATUS_FAILURE);
      this.handleApplePayError(e, onPurchaseLimitExceeded);
    }
  }

  private handleApplePayError(error: any, onPurchaseLimitExceeded?: VoidFunction) {
    logToNewRelic('Paysafe Apple Pay error', JSON.stringify(error), 'error');
    if (error?.code === '9152') return;

    if (typeof onPurchaseLimitExceeded === 'function' && isPurchaseLimitError(error)) {
      return onPurchaseLimitExceeded();
    }

    const errorName = error.response?.data?.errors[0]?.name || error.name;

    if (errorName === 'ForceKYCError') {
      this.rootStore.uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenKycDialog,
      });
      return;
    }

    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
      options: { type: 'purchase' },
    });
  }

  public async getPreviousPaymentDataPaysafe() {
    try {
      this.setPaymentLoading(true);
      this.setShowPreviousPayment(false);
      const response = await getRequest<ApiResponse<IPreviousPaymentData, 'details'>>('user/cards-paysafe');

      if (response.data.success) {
        const previousPaymentData = this.convertTimeToLiveSecondsToDate(response.data.details);
        this.setPreviousPaymentData(previousPaymentData);
      }
    } catch (error) {
    } finally {
      this.setPaymentLoading(false);
    }
  }

  public async getPreviousPaymentDataNuvei() {
    try {
      this.setPaymentLoading(true);
      const response = await getRequest<ApiResponse<IPreviousPaymentData, 'paymentMethods'>>('user/upo');

      if (response.data.success && !isEmpty(response.data.paymentMethods)) {
        this.setPreviousPaymentData(response.data.paymentMethods);
      }
    } catch (error) {
    } finally {
      this.setPaymentLoading(false);
    }
  }

  @action
  public setPreviousPaymentData(data: IPreviousPaymentData | null) {
    this.previousPaymentData = data;
  }

  @action setLastPaymentData(data: ILastPaymentMethod | null) {
    this.lastPaymentData = data;
  }

  public async paymentSkrill(onPurchaseLimitExceeded?: VoidFunction, gameId?: string) {
    this.setPaymentLoading(true);

    const packageId = this.rootStore.appStore.selectedPackage?.packageId as number;
    const { appStore } = this.rootStore;

    await skrillMakePurchase({
      packageId,
      platform: this.platform,
      onFail: () =>
        this.rootStore.uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
          options: { type: 'purchase' },
        }),
      onKYCRequired: () =>
        this.rootStore.uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenKycDialog,
        }),
      onSuccess: (href) =>
        this.rootStore.uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenThirdPartyIframeDialog,
          options: {
            thirdPartyUrl: href,
          },
        }),
      eventTriggerData: appStore.currentOrderPackage?.eventTriggerData,
      gameId,
      onPurchaseLimitExceeded,
    });

    this.setPaymentLoading(false);
  }

  public async paymentMazooma(onPurchaseLimitExceeded?: VoidFunction, gameId?: string) {
    try {
      this.setPaymentLoading(true);
      const packageId = this.rootStore.appStore.selectedPackage?.packageId as number;

      const paymentRequest = {
        packageId: packageId,
      };

      const hrefOrigin = location.origin;
      const gameIdParams = gameId ? `&gameId=${gameId}` : '';

      const data = {
        ...paymentRequest,
        links: [
          {
            rel: 'default',
            href: `${hrefOrigin}/?method=bank&status=default&type=purchase`,
            method: 'GET',
          },
          {
            rel: 'on_completed',
            href: `${hrefOrigin}/?method=bank&status=complete&type=purchase&packageId=${packageId}${gameIdParams}`,
            method: 'GET',
          },
          {
            rel: 'on_failed',
            href: `${hrefOrigin}/?method=bank&status=faile&type=purchase`,
            method: 'GET',
          },
          {
            rel: 'on_cancelled',
            href: `${hrefOrigin}/?method=bank&status=cancelled&type=purchase`,
            method: 'GET',
          },
        ],
      };

      const response = await postRequest<{
        data: {
          success: boolean;
          paymentOption: {
            redirectUrl: string;
            userPaymentOptionId: number;
          };
        };
      }>('user/nuvei-mazooma', data);

      if (response.data.success) {
        const href = response?.data?.paymentOption?.redirectUrl;

        this.rootStore.uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenThirdPartyIframeDialog,
          options: {
            thirdPartyUrl: href,
          },
        });
      }

      this.setPaymentLoading(false);
    } catch (error: any) {
      this.setPaymentLoading(false);
      if (typeof onPurchaseLimitExceeded === 'function' && isPurchaseLimitError(error)) {
        return onPurchaseLimitExceeded();
      }

      const errorName = error.response?.data?.errors[0]?.name || error.name;

      if (errorName === 'ForceKYCError') {
        this.rootStore.uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenKycDialog,
        });
        return;
      }

      const errorDetails = error.response?.data?.errors[0]?.description || error.detailedMessage;

      this.rootStore.uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
        options: { type: 'purchase', failureDetails: errorDetails },
      });
    }
  }

  @action
  public handleSetupFieldsCreditCardPaysafe(optionalField: boolean) {
    if (this.paysafeInstance) {
      this.setPaymentProcess(true);
      this.setPaymentLoading(true);

      const OPTIONS = {
        currencyCode: 'USD',
        accounts: {
          default: Number(VITE_PAYSAFE_ACCOUNT_FMA),
        },
        environment: VITE_PAYSAFE_ENVIROMENT,
        fields: {
          cardNumber: {
            selector: '#cardNumber',
            placeholder: 'Enter Your Card Number',
            optional: optionalField,
          },
          expiryDate: {
            selector: '#expiryDate',
            placeholder: 'MM/YY',
            optional: optionalField,
          },
          cvv: {
            selector: '#cvv',
            placeholder: 'Enter CVV Number',
            optional: optionalField,
          },
        },
        style: {
          input: {
            color: 'white !important',
          },
          'input::placeholder': {
            color: '#D7A2FA',
            'font-size': '0.8rem',
          },
          'input:-webkit-autofill': {
            transition: 'all 9999999s;', // Delay autocomplete styling
          },
        },
      };

      void this.paysafeInstance.fields
        .setup(VITE_PAYSAFE_API_KEY, OPTIONS)
        .then((instance) => {
          this.setPaysafeCreditCardInstance(instance);
        })
        .catch((error) => {
          this.rootStore.uiStore.setOpenDialog({
            dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
            options: { type: 'purchase' },
          });
          logToNewRelic('Paysafe instance setup fields error', JSON.stringify(error), 'error');
        })
        .finally(() => {
          this.setPaymentProcess(false);
          this.setPaymentLoading(false);
        });
    }
  }

  @action
  public handleSetupFieldsCreditCardSafeCharge() {
    if (this.safeChargeInstance) {
      this.setPaymentProcess(true);
      this.setPaymentLoading(true);

      const ScFieldStyles = {
        base: {
          color: '#ffffff',
          fontWeight: 500,
          fontFamily: '"Outfit Variable", Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
          fontSize: '14px',
          fontSmoothing: 'antialiased',
          '::placeholder': {
            color: '#d7a2fa',
            fontSize: '.8rem',
          },
          ':-webkit-autofill': {
            color: '#ffffff',
          },
          marginTop: '10px',
        },
        invalid: {
          color: '#ffffff',
          '::placeholder': {
            color: '#d7a2fa',
            fontSize: '.8rem',
          },
        },
        empty: {},
      };

      const safeChargeFields = this.safeChargeInstance.fields({
        fontSize: '14px',
        locale: 'en',
      });

      // Card number----------------------------------------------------

      const cardNumber = safeChargeFields.create('ccNumber', {
        style: ScFieldStyles,
      });

      cardNumber.attach('#cardNumber');

      const cardExpiry = safeChargeFields.create('ccExpiration', {
        style: ScFieldStyles,
      });

      cardExpiry.attach('#expiryDate');

      // CVV--------------------------------------------------------------

      const cardCvc = safeChargeFields.create('ccCvc', {
        style: ScFieldStyles,
      });

      cardCvc.attach('#cvv');
      this.setSafeChargeCard(cardNumber);

      this.setPaymentProcess(false);
      this.setPaymentLoading(false);
    }
  }

  public async handlePaymentCreditCardSafeCharge(storeCredentials: boolean) {
    try {
      const { appStore, userStore, uiStore } = this.rootStore;
      if (this.safeChargeInstance && userStore.profile && appStore?.selectedPackage?.price) {
        this.setPaymentLoading(true);
        const { email, firstName, lastName, cleanGeoData, city, zipCode, address } = userStore.profile;
        const response = await postRequest<{
          openOrder: {
            sessionToken: string;
          };
        }>('user/open-order', {
          packageId: appStore.selectedPackage.packageId,
          paymentType: TSafeChargePaymentType.deposit,
          storeCredentials,
        });

        const sessionToken = get(response, 'data.openOrder.sessionToken', null);

        const billingAddress = {
          country: cleanGeoData?.countryCode,
          zip: zipCode,
          city,
          address: address || '',
          firstName,
          lastName,
          email,
        };

        let option: any = {
          sessionToken,
          cardHolderName: `${userStore.profile.firstName} ${userStore.profile.lastName}`,
          paymentOption: this.safeChargeCard,
          billingAddress,
        };
        if (this.previousPaymentData) {
          const { customerToken, paymentHandleToken } = this.previousPaymentData.paymentHandles[0];
          option = {
            sessionToken,
            userTokenId: customerToken,
            paymentOption: {
              userPaymentOptionId: paymentHandleToken,
            },
          };
        }

        this.safeChargeInstance.createPayment(option, (paymentStatus) => {
          if (paymentStatus) {
            if (paymentStatus.result == 'ERROR') {
              logToNewRelic('SafeCharge payment error', JSON.stringify(paymentStatus), 'error');
              uiStore.setOpenDialog({
                dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
                options: { type: 'purchase' },
              });
            } else {
              logToNewRelic(
                'pre sendSuccessPurchaseEvent',
                {
                  message: 'in handlePaymentCreditCardSafeCharge',
                  data: paymentStatus,
                },
                'info',
              );
              if (paymentStatus.transactionStatus == 'APPROVED') {
                // TODO: if we sometime will return this flow, we should populate transactionsEventsRegistry and inProcessTransactionId
                this.handleSuccessPurchase();
              } else if (paymentStatus.transactionStatus == 'DECLINED') {
                logToNewRelic('SafeCharge payment declined', JSON.stringify(paymentStatus), 'info');
                uiStore.setOpenDialog({
                  dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
                  options: { type: 'purchase' },
                });
              }
              this.setSafeChargeCard(null);
            }
          }

          this.setPaymentLoading(false);
        });
      }
    } catch (e) {
      devError('handlePaymentCreditCardSafeCharge Error', e);
      this.setPaymentLoading(false);
    }
  }

  private async getOverriddenBillingDetails(countryCode: string) {
    if (!this.newCardBillingDetails) {
      return undefined;
    }

    // The player can't select a new country when entering billing address, so the territories from the country specified in their profile are enough
    const territoriesData = await territoriesApi.fetchGeoData(countryCode);
    const territory = territoriesData.map[this.newCardBillingDetails.territoryKey];

    // Do not accept "fallback" territories
    if (!territory?.code) {
      return undefined;
    }

    return {
      country: territory.country.code,
      zip: this.newCardBillingDetails.zipCode,
      city: this.newCardBillingDetails.city,
      state: territory.code,
      street: this.newCardBillingDetails.address,
    };
  }

  private async performPaysafeCreditCardPayment(
    { storeCredentials = true, isPaysafe3DSEnabled = false },
    onPurchaseLimitExceeded?: VoidFunction,
  ) {
    const { appStore, walletStore, uiStore, userStore } = this.rootStore;

    if (!userStore.profile?.cleanGeoData || !appStore?.selectedPackage?.price) {
      return;
    }

    this.setPaymentLoading(true);

    const { email, firstName, lastName, cleanGeoData, city, zipCode, address } = userStore.profile;
    const merchantRefNum = generateGuid();

    const amount = MultiplyBy100AndRound(appStore.selectedPackage?.price);

    let tokenizeWithSavedCardOptions = {};
    if (this.tokenizeWithSavedCard && this.previousPaymentData) {
      const { customerToken, paymentHandleToken } = this.previousPaymentData.paymentHandles[0];
      tokenizeWithSavedCardOptions = {
        singleUseCustomerToken: customerToken,
        paymentTokenFrom: paymentHandleToken,
      };
    }

    const baseBillingDetails = {
      country: cleanGeoData.countryCode,
      zip: zipCode,
      city,
      state: cleanGeoData.territoryCode || undefined,
      street: address || '',
    };

    const overriddenBillingDetails = await this.getOverriddenBillingDetails(cleanGeoData?.countryCode);
    const billingDetails = overriddenBillingDetails || baseBillingDetails;

    if (!this.paysafeCreditCardInstance) {
      this.setPaymentLoading(false);
      return;
    }

    const tokenizationOptions = {
      amount: amount,
      merchantRefNum,
      transactionType: 'PAYMENT',
      paymentType: EnumPaymentMethods.card,
      ...(isPaysafe3DSEnabled
        ? {
            threeDs: {
              merchantUrl: location.origin,
              deviceChannel: 'BROWSER',
              messageCategory: 'PAYMENT',
              transactionIntent: 'GOODS_OR_SERVICE_PURCHASE',
              authenticationPurpose: 'PAYMENT_TRANSACTION',
              requestorChallengePreference: 'NO_PREFERENCE',
              force3DS1: false,
            },
          }
        : {}),
      customerDetails: {
        ...(isPaysafe3DSEnabled ? { holderName: `${firstName} ${lastName}` } : {}),
        billingDetails,
        profile: {
          firstName,
          lastName,
          email,
        },
      },
      ...tokenizeWithSavedCardOptions,
    };

    try {
      const { token }: { token: string } = await this.paysafeCreditCardInstance.tokenize(tokenizationOptions);

      const packageId = appStore.selectedPackage?.packageId;
      const data = {
        packageId,
        paymentMethod: EnumPaymentMethods.card,
        merchantRefNum,
        paymentHandleToken: token,
        storeCredentials,
        ...this.rootStore.appStore.currentOrderPackage?.eventTriggerData,
      };

      let response = null;

      try {
        response = await postRequest<ApiResponse<UserTransaction, 'details'>>('user/proceed-payment-paysafe', data);
      } catch (proceedPaymentError) {
        const errorDetails = this.getPaysafeErrorDetails(proceedPaymentError);

        return uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
          options: { type: 'purchase', failureDetails: errorDetails },
        });
      }

      logToNewRelic(
        'pre sendSuccessPurchaseEvent',
        {
          message: 'in handlePaymentCreditCardPaysafe',
          data: response.data,
        },
        'info',
      );

      if (response.data.success) {
        const { status, isFirstDeposit } = response.data.details;

        const isSuccessPayment = [
          TransactionStatus.SUCCESS,
          TransactionStatus.APPROVED,
          TransactionStatus.INPROGRESS,
          TransactionStatus.PENDING,
        ].includes(status);

        if (!socket.connected && isSuccessPayment) {
          void walletStore.fetchWallet();
          this.setInProcessTransactionId(merchantRefNum);
          this.transactionsEventsRegistry.set(merchantRefNum, {
            // transactionId and merchantRefNum are the same entity
            transactionId: merchantRefNum,
            amount,
            isFirstDeposit,
          });
          this.handleSuccessPurchase();
        } else if (!isSuccessPayment) {
          uiStore.setOpenDialog({
            dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
            options: { type: 'purchase' },
          });
        }
      } else {
        uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
          options: { type: 'purchase' },
        });
      }
    } catch (error) {
      if (typeof onPurchaseLimitExceeded === 'function' && isPurchaseLimitError(error)) {
        return onPurchaseLimitExceeded();
      }

      if (!(error instanceof AxiosError)) {
        uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
          options: { type: 'purchase', failureDetails: 'Something went wrong' },
        });

        return;
      }

      const errorName = error.response?.data?.errors[0]?.name || error.name;

      if (errorName === 'ForceKYCError') {
        this.rootStore.uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenKycDialog,
        });
        return;
      }

      const errorDetails = this.getPaysafeErrorDetails(error);
      const avsCheckFailed = error.response?.data?.errors[0]?.fields?.avsCheckFailed;

      if (avsCheckFailed) {
        uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenTransactionFailedDueToAddressDialog,
        });
        return;
      }

      uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
        options: { type: 'purchase', failureDetails: errorDetails },
      });
    } finally {
      this.setPaymentLoading(false);
    }
  }

  public handlePaymentCreditCardPaysafe(
    { storeCredentials = true, isPaysafe3DSEnabled = false },
    onPurchaseLimitExceeded?: VoidFunction,
  ) {
    void this.performPaysafeCreditCardPayment({ storeCredentials, isPaysafe3DSEnabled }, onPurchaseLimitExceeded);
  }

  @action
  public async handleRemoveUserCreditCard(paymentHandles: ISavedCreditCardData[]) {
    const nuvei_payment_credit_card = resolveFlag('nuvei_payment_credit_card', false);
    if (nuvei_payment_credit_card) {
      // ADD NUVEI FUNCTIONALITY
    } else {
      const paymentHandleId = paymentHandles[0].paymentHandleId;
      await deleteRequest('user/paysafe-card', {
        paymentHandleId,
      });
    }
  }

  @action
  public async confirmRedeemSafeCharge(data: ISendConfirmRedeemSafeChargeData): Promise<IConfirmRedeemResponse> {
    data.platform = this.platform;
    data.withdrawBalanceType = this.toApiWithdrawBalanceType(data.withdrawBalanceType);
    const response = await postRequest('user/create-withdraw-request', data);

    const transactionId = get(response, 'data.transactionId', null);

    if (response.data.success) {
      this.setRedeemSafeChargeSuccessTransactionId(transactionId);
      void this.rootStore.walletStore.fetchWallet();
      return { transactionId: transactionId || -1, status: 'success' };
    } else {
      this.setRedeemFailure({ isFailed: true });
      return { transactionId: -1, status: 'failed' };
    }
  }

  @action
  public async confirmPayoutsRedeem(data: IPayoutsRedeemRequest): Promise<IConfirmRedeemResponse> {
    try {
      data.platform = this.platform;
      data.withdrawBalanceType = this.toApiWithdrawBalanceType(data.withdrawBalanceType);
      const response = await postRequest('payment/create-payouts-redeem', data);

      const transactionId = get(response, 'data.details.transactionId', null);

      if (response.data.success) {
        this.setRedeemSafeChargeSuccessTransactionId(transactionId);
        void this.rootStore.walletStore.fetchWallet();
        return { transactionId: transactionId || -1, status: 'success' };
      } else {
        this.setRedeemFailure({ isFailed: true });
        return { transactionId: -1, status: 'failed' };
      }
    } catch (error: any) {
      const errorTitle = error.response.data.errors[0]?.description;

      const isOtpRequired = error.response?.data?.errors[0]?.name === 'OtpRequiredError';
      const isOtpInvalid = error.response?.data?.errors[0]?.name === 'InvalidOtpError';
      const isSendOtpError = error.response?.data?.errors[0]?.name === 'FailedToSendOtpError';
      if (!isOtpRequired && !isOtpInvalid && !isSendOtpError) {
        this.setRedeemFailure({ isFailed: true, ...(errorTitle ? { options: { errorTitle } } : {}) });
      }

      if (this.redeemData && isOtpRequired) {
        this.setRedeemData({ ...this.redeemData });
      }

      return { transactionId: -1, status: 'failed', extra: { isOtpRequired, isOtpInvalid, isSendOtpError } };
    }
  }

  @action
  public setRedemptionCaptureResult(result?: Partial<RedemptionCaptureResult> | null) {
    if (!result) {
      this.redemptionCaptureResult = null;
      return;
    }

    const { redemptionRequestId, preparationResult, redemptionAmount } = result;

    if (!redemptionRequestId || !preparationResult || !redemptionAmount) {
      this.redemptionCaptureResult = null;
      return;
    }

    this.redemptionCaptureResult = { redemptionRequestId, preparationResult, redemptionAmount };
  }

  @action
  public async confirmRedeemSkrill({ email, otp }: { email: string; otp?: string }): Promise<IConfirmRedeemResponse> {
    try {
      const redemptionAmount = this.redeemCoins;

      const data = {
        otp,
        coins: redemptionAmount,
        skrillConsumerId: email,
        platform: this.platform,
        withdrawBalanceType: this.toApiWithdrawBalanceType(this.withdrawBalanceType),
      };

      const response = await postRequest<{
        data?: {
          details?: { transactionId: number } & Partial<ApiRedemptionCaptureResult>;
          success?: boolean;
        };
      }>('payment/create-payout-skrill', data);
      const { transactionId = -1, redemptionRequestId, preparationResult } = response.data?.details || {};

      if (response.data?.success) {
        this.setRedeemSafeChargeSuccessTransactionId(transactionId);
        this.setRedemptionCaptureResult({ redemptionRequestId, preparationResult, redemptionAmount });

        void this.rootStore.walletStore.fetchWallet();
        return {
          transactionId,
          status: 'success',
          extra: { isOtpRequired: false, isOtpInvalid: false, isSendOtpError: false },
        };
      } else {
        this.setRedeemFailure({ isFailed: true });
        return {
          transactionId,
          status: 'failed',
          extra: { isOtpRequired: false, isOtpInvalid: false, isSendOtpError: false },
        };
      }
    } catch (error: any) {
      const errorTitle = error.response.data.errors[0]?.description;

      const isOtpRequired = error.response?.data?.errors[0]?.name === 'OtpRequiredError';
      const isOtpInvalid = error.response?.data?.errors[0]?.name === 'InvalidOtpError';
      const isSendOtpError = error.response?.data?.errors[0]?.name === 'FailedToSendOtpError';

      if (!isOtpRequired && !isOtpInvalid && !isSendOtpError) {
        this.setRedeemFailure({ isFailed: true, ...(errorTitle ? { options: { errorTitle } } : {}) });
      }

      if (this.redeemData && isOtpRequired) {
        this.setRedeemData({ ...this.redeemData, email });
      }

      return { transactionId: -1, status: 'failed', extra: { isOtpRequired, isOtpInvalid, isSendOtpError } };
    }
  }

  public async confirmRedeemPaysafeCard({
    otp,
    withdrawBalanceType,
  }: {
    otp?: string;
    withdrawBalanceType: WithdrawBalanceType;
  }): Promise<IConfirmRedeemResponse> {
    const email = this.rootStore.userStore.profile?.email;

    try {
      const redemptionAmount = this.redeemCoins;

      const data = {
        otp,
        coins: redemptionAmount,
        platform: this.platform,
        withdrawBalanceType: this.toApiWithdrawBalanceType(withdrawBalanceType),
      };

      const response = await postRequest<{
        data?: {
          details?: { transactionId: number } & Partial<ApiRedemptionCaptureResult>;
          success?: boolean;
        };
      }>('payment/create-payout-paysafe-card', data);
      const { transactionId = -1, redemptionRequestId, preparationResult } = response.data?.details || {};

      if (response.data?.success) {
        this.setRedeemSafeChargeSuccessTransactionId(transactionId);
        this.setRedemptionCaptureResult({ redemptionRequestId, preparationResult, redemptionAmount });

        void this.rootStore.walletStore.fetchWallet();
        return {
          transactionId,
          status: 'success',
          extra: { isOtpRequired: false, isOtpInvalid: false, isSendOtpError: false },
        };
      } else {
        this.setRedeemFailure({ isFailed: true });
        return {
          transactionId,
          status: 'failed',
          extra: { isOtpRequired: false, isOtpInvalid: false, isSendOtpError: false },
        };
      }
    } catch (error: any) {
      const errorTitle = error.response.data.errors[0]?.description;

      const isOtpRequired = error.response?.data?.errors[0]?.name === 'OtpRequiredError';
      const isOtpInvalid = error.response?.data?.errors[0]?.name === 'InvalidOtpError';
      const isSendOtpError = error.response?.data?.errors[0]?.name === 'FailedToSendOtpError';

      if (!isOtpRequired && !isOtpInvalid && !isSendOtpError) {
        this.setRedeemFailure({ isFailed: true, ...(errorTitle ? { options: { errorTitle } } : {}) });
      }

      if (this.redeemData && isOtpRequired) {
        this.setRedeemData({ ...this.redeemData, email });
      }

      return { transactionId: -1, status: 'failed', extra: { isOtpRequired, isOtpInvalid, isSendOtpError } };
    }
  }

  private currentlyUsedUpoId?: string;

  @action
  public async confirmRedeemIBT(args: {
    coins: number;
    paymentMethodType: TSafeChargeMethodType;
    personalDetails?: IUserDetailsData;
    paymentIdBankAccount?: string;
    otp?: string;
    withdrawBalanceType: WithdrawBalanceType;
  }): Promise<IConfirmRedeemResponse> {
    const { paymentMethodType, coins, paymentIdBankAccount, personalDetails, otp, withdrawBalanceType } = args;
    this.currentlyUsedUpoId = paymentIdBankAccount;

    try {
      const data = {
        paymentMethodType,
        coins,
        platform: this.platform,
        ...(otp ? { otp } : {}),
        ...(personalDetails ? { personalDetails } : {}),
        ...(paymentIdBankAccount ? { paymentId: paymentIdBankAccount.toString() } : {}),
        withdrawBalanceType: this.toApiWithdrawBalanceType(withdrawBalanceType),
      };

      const response = await postRequest<{
        data?: {
          details?: { transactionId: number; links?: string } & Partial<ApiRedemptionCaptureResult>;
          success?: boolean;
        };
      }>('/v2/payment/account-capture', data);

      const { links, transactionId, redemptionRequestId, preparationResult } = response.data?.details || {};

      if (response.data?.success && links) {
        this.rootStore.uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenThirdPartyIframeDialog,
          options: {
            thirdPartyUrl: links,
          },
        });

        return {
          transactionId: transactionId || -1,
          status: 'pending',
          extra: { isOtpRequired: false, isOtpInvalid: false, isSendOtpError: false },
        };
      } else if (response.data?.success && transactionId) {
        this.setRedemptionCaptureResult({ redemptionRequestId, preparationResult, redemptionAmount: args.coins });
        this.setRedeemSafeChargeSuccessTransactionId(transactionId);
        void this.rootStore.walletStore.fetchWallet();

        return {
          transactionId,
          status: 'success',
          extra: { isOtpRequired: false, isOtpInvalid: false, isSendOtpError: false },
        };
      } else {
        if (this.withdrawBalanceType === 'SC') {
          this.rootStore.uiStore.setCloseDialog();
        }
        this.setRedeemFailure({ isFailed: true });

        return {
          transactionId: -1,
          status: 'success',
          extra: { isOtpRequired: false, isOtpInvalid: false, isSendOtpError: false },
        };
      }
    } catch (error: any) {
      const errorData = error.response.data.errors[0];
      let errorTitle = errorData?.message || errorData?.description;
      errorTitle = errorTitle.replace('Payment Error: ', '');

      const isOtpRequired = errorData?.name === 'OtpRequiredError';
      const isOtpInvalid = errorData?.name === 'InvalidOtpError';
      const isSendOtpError = errorData?.name === 'FailedToSendOtpError';

      if (!isOtpRequired && !isOtpInvalid && !isSendOtpError) {
        if (this.redeemData) {
          this.setRedeemData({ ...this.redeemData, paymentIdBankAccount });
        }

        this.setRedeemFailure({ isFailed: true, ...(errorTitle ? { options: { errorTitle } } : {}) });
      }

      if (this.redeemData && isOtpRequired) {
        this.setRedeemData({ ...this.redeemData, paymentIdBankAccount, personalDetails, email: paymentIdBankAccount });
      }

      return { transactionId: -1, status: 'failed', extra: { isOtpRequired, isOtpInvalid, isSendOtpError } };
    } finally {
      this.currentlyUsedUpoId = undefined;
    }
  }

  @action
  public async confirmRedeemBankAccount(): Promise<IConfirmRedeemResponse> {
    try {
      const { userStore } = this.rootStore;

      if (this.safeChargeInstance && userStore.profile) {
        const response = await postRequest<{
          openOrder: {
            sessionToken: string;
            clientIp: string;
          };
        }>('user/open-order', { coins: this.amountRedeem, paymentType: TSafeChargePaymentType.redeem });

        const sessionToken = get(response, 'data.openOrder.sessionToken', null);
        let clientIP = get(response, 'data.openOrder.clientIp', userStore.profile.clientIP);

        if (!clientIP) {
          const DEFAULT_CLIENT_IP = '174.211.226.127';
          try {
            const res = await axios.get('https://api.ipify.org?format=json');
            clientIP = res.data.ip || DEFAULT_CLIENT_IP;
          } catch (err) {
            clientIP = DEFAULT_CLIENT_IP;
          }
        }

        const hrefOrigin = location.origin;
        const { email, firstName, lastName, city, zipCode, address, firebaseUid, phone, dateOfBirth, cleanGeoData } =
          userStore.profile;

        const birthdate = dayjs(dateOfBirth).format('YYYY-MM-DD');

        const options = {
          sessionToken,
          merchantId: import.meta.env.VITE_MERCHANT_ID,
          merchantSiteId: import.meta.env.VITE_MERCHANT_SITE_ID,
          paymentMethod: 'apmgw_eCheckSelect',
          userTokenId: firebaseUid,
          currencyCode: 'USD',
          countryCode: 'US',
          languageCode: 'en',
          amount: this.amountRedeem,
          urlDetails: {
            successUrl: `${hrefOrigin}/?method=bank&status=complete&type=redeem`,
            failureUrl: `${hrefOrigin}/?method=bank&status=faile&type=redeem`,
            pendingUrl: `${hrefOrigin}/?method=bank&status=pending&type=redeem`,
          },
          userDetails: {
            firstName,
            lastName,
            address,
            phone,
            zip: zipCode,
            city,
            state: cleanGeoData?.territoryCode,
            email,
            country: cleanGeoData?.countryCode,
            birthdate,
          },
          deviceDetails: {
            ipAddress: clientIP,
          },
        };

        const handleApv = (apvResponse: IAccountCaptureResponseCbArgs) => {
          if (apvResponse.status === 'SUCCESS') {
            this.rootStore.uiStore.setOpenDialog({
              dialog: EnumOpenDialog.isOpenThirdPartyIframeDialog,
              options: {
                thirdPartyUrl: apvResponse.redirectUrl,
              },
            });
          } else {
            const { status } = apvResponse;
            const responseStr = JSON.stringify(apvResponse);
            logToNewRelic(
              'confirmRedeemBankAccount-handleApv',
              `failed with status of "${status}, response: ${responseStr}"`,
              'error',
            );
            this.setRedeemFailure({ isFailed: true });
          }
        };

        this.safeChargeInstance.accountCapture(options, handleApv);
        return { transactionId: -1, status: 'pending' };
      } else {
        logToNewRelic('confirmRedeemBankAccount', 'safeChargeInstance or userStore.profile is undefined', 'error');
        this.setRedeemFailure({ isFailed: true });
        return { transactionId: -1, status: 'failed' };
      }
    } catch (error) {
      this.setRedeemFailure({ isFailed: true });
      return { transactionId: -1, status: 'failed' };
    }
  }

  public async getSafeChargePaymentMethods() {
    if (resolveFlag('nuvei_payment_credit_card', false)) {
      const packageId = this.rootStore.appStore.selectedPackage?.packageId;

      const response = await postRequest<{
        openOrder: {
          sessionToken: string;
        };
      }>('user/open-order', { packageId, paymentType: TSafeChargePaymentType.deposit });

      const sessionToken = get(response, 'data.openOrder.sessionToken', null);

      this.safeChargeInstance?.getApms(
        {
          countryCode: 'GB',
          languageCode: 'en',
          sessionToken,
          merchantId: import.meta.env.VITE_MERCHANT_ID,
          merchantSiteId: import.meta.env.VITE_MERCHANT_SITE_ID,
        },
        (res: any) => {
          if (res.status === 'SUCCESS') {
            this.setSafeChargePaymentMethods(res.paymentMethods);
          }
        },
      );
    }
  }

  private setSafeChargePaymentMethods(methods: ISafeChargePaymentMethod[]) {
    this.safeChargePaymentMethods = methods;
  }

  @action
  public async getUpo() {
    try {
      this.setPaymentLoading(true);
      const response = await getRequest<ApiResponse<never>>('user/upo');

      if (response.data.success) {
        const paymentMethods = get(response, 'data.paymentMethods', []);

        const [card] = paymentMethods.filter(
          (item: { paymentMethodName: string }) => item.paymentMethodName === TSafeChargeMethodType.card,
        );
        const [bankAccount] = paymentMethods.filter(
          (item: { paymentMethodName: string }) => item.paymentMethodName === TSafeChargeMethodType.bank,
        );

        this.setUpo({ card, bankAccount });
      }
    } finally {
      this.setPaymentLoading(false);
    }
  }

  @action
  public async getCardUpo(withInternalData = false) {
    try {
      const response = await getRequest<ApiResponse<IUpoDataCardRedeem[], 'details'>>('payment/redeem-cards', {
        withInternalData,
      });

      if (response.data.success) {
        if (!withInternalData) {
          this.setCardRedeemUpo(response.data.details);
        } else {
          this.setCardRedeemUpoInternal(response.data.details);
        }
      }
    } catch (error) {
      devError('getCardUpo Error', error);
    }
  }

  @action
  public async getBankAccounts({
    amount,
    paymentMethodType,
    otp,
    withdrawBalanceType,
  }: {
    amount: number;
    paymentMethodType: TSafeChargeMethodType;
    otp?: string;
    withdrawBalanceType: WithdrawBalanceType;
  }) {
    try {
      this.setPaymentProcess(true);
      const response = await postRequest<ApiResponse<IUpoDataCardRedeem[], 'details'>>('/v2/payment/account-capture', {
        paymentMethodType,
        coins: amount,
        otp,
        platform: this.platform,
        withdrawBalanceType: this.toApiWithdrawBalanceType(withdrawBalanceType),
      });

      if (response.data.success) {
        this.setRedemptionBankAccounts(response.data.details);
      }
    } catch (error: any) {
      devError('getBankAccounts Error', error);
    } finally {
      this.setPaymentProcess(false);
    }
  }

  private static readonly METHODS_UTILIZING_UPOS = new Set([
    TSafeChargeMethodType.bankPaysafe,
    TSafeChargeMethodType.bank,
  ]);

  public async requestUPODeletion(idUsedInPayment: string, upoId: string | number, registrationId?: string) {
    try {
      const paymentMethod = this.redeemData?.paymentMethodType;

      if (!paymentMethod || !PayRedeemStore.METHODS_UTILIZING_UPOS.has(paymentMethod)) {
        return false;
      }

      if (this.currentlyUsedUpoId && idUsedInPayment === this.currentlyUsedUpoId) {
        return false;
      }

      this.setIsDeletingBankAccount(true);
      const deletionRes = await deleteRequest<ApiResponse<{ status: UPODeletionResult }>>(
        `/v2/payment/user-payment-options`,
        { paymentMethod, upoId: `${upoId}`, registrationId },
      );

      if (deletionRes.data.data.status !== 'success') {
        return false;
      }

      if (this.bankAccounts?.bankAccounts.length) {
        this.setRedemptionBankAccounts({
          ...this.bankAccounts,
          bankAccounts: this.bankAccounts.bankAccounts.filter((acc) => acc.uniqueUpoId !== upoId),
        });
      }

      return true;
    } catch (upoDeletionError) {
      logToNewRelic(
        'UPO deletion error',
        `Got error on FE while deleting UPO: ${JSON.stringify({ upoDeletionError, upoId, registrationId })}`,
        'error',
      );
      return false;
    } finally {
      this.setIsDeletingBankAccount(false);
    }
  }

  @action
  public setUpo(data: IUpoData) {
    this.upoData = { ...this.upoData, ...data };
  }

  @action
  public setCardRedeemUpo(data: IUpoDataCardRedeem[]) {
    this.cardRedeemUpo = data;
  }

  @action
  public setCardRedeemUpoInternal(data: IUpoDataCardRedeem[]) {
    this.cardRedeemUpoInternal = data;
  }

  private subscribeToSocketBankAccount(data: any) {
    if (data.data.status && data.data.transactionId) {
      const redemptionAmount = this.redeemCoins;

      this.setRedeemSafeChargeSuccessTransactionId(data.data.transactionId);
      this.setRedemptionCaptureResult({ ...data.data, redemptionAmount });
      void this.rootStore.walletStore.fetchWallet();

      if (this.withdrawBalanceType === 'SC') {
        this.rootStore.uiStore.setCloseDialog();
      }
    } else {
      this.setRedeemFailure({
        isFailed: true,
        options: {
          socketError: true,
          errorTitle:
            "It looks like you didn't complete the redemption process. Is there something we can help you with?",
        },
      });

      this.rootStore.uiStore.setCloseDialog();
    }
  }

  private errorListeners = new Set<CvvErrorListener>();

  public addCvvErrorListener(listener: CvvErrorListener) {
    this.errorListeners.add(listener);
  }

  public removeCvvErrorListener(listener: CvvErrorListener) {
    this.errorListeners.delete(listener);
  }

  private subscribeToSocket({ data }: { data: UserTransaction }) {
    const selectedPackage = this.rootStore.appStore.packageForPurchaseOutcome;
    const handleWithinApp = resolveFlag('handle_failures_within_payments_app', true);
    const paymentsVersion = resolveFlag<number>('payments_app_version', CURRENT_PAYMENT_APP_VERSION);

    if (
      paymentsVersion >= CURRENT_PAYMENT_APP_VERSION &&
      data.paymentMethod &&
      data.paymentMethod !== EnumPaymentMethods.skrill
    ) {
      // If the payment method is not Skrill, then no need to listen to socket events because
      // we will get the purchase status with postMessage from securePay iframe
      return;
    }

    if (!selectedPackage) {
      logToNewRelic(
        'pre sendSuccessPurchaseEvent',
        {
          message: 'in subscribeToSocket no selectedPackage',
          data,
        },
        'info',
      );
      // If there is no selected package, then it could mean that the purchase is happening in a different tab.
      return;
    }

    if (this.inProcessTransactionId !== data.transactionId) {
      // If the transaction ID is not the same as the one we are waiting for,
      // then it could mean that the purchase is happening in a different tab.
      return;
    }

    this.setPaymentProcess(false);

    logToNewRelic(
      'pre sendSuccessPurchaseEvent',
      {
        message: 'in subscribeToSocket',
        data,
      },
      'info',
    );

    if (data.status === TransactionStatus.SUCCESS || data.status === TransactionStatus.APPROVED) {
      pendingTransactionsRegistry.popPendingTransaction(data.transactionId);

      this.transactionsEventsRegistry.set(this.inProcessTransactionId, {
        ...data,
        transactionId: this.inProcessTransactionId,
        amount: data.amount ?? selectedPackage.price ?? 0,
        selectedPackage,
      });

      this.rootStore.payRedeemStore.setAfterPurchaseFlowInProgress(true);
      this.rootStore.portalStore.closePortal();
      this.handleSuccessPurchase();
      void this.rootStore.userStore.fetchProfile();
    } else {
      if (data.description && data.providerError) {
        const avsCheckFailed = data.avsCheckFailed;

        if (avsCheckFailed && !handleWithinApp) {
          this.rootStore.uiStore.setOpenDialog({
            dialog: EnumOpenDialog.isOpenTransactionFailedDueToAddressDialog,
          });
        } else {
          const details = data.description;
          const providerError = data.providerError;
          const errorCode = data.error || providerError;

          // Ignore this error on socket, we'll get it by HTTP response
          if ([SystemErrorCodes.CVV_VALUE_NOT_PRESENT].includes(errorCode)) {
            return;
          }

          const pendingTransactionParams = pendingTransactionsRegistry.popPendingTransaction(data.transactionId);

          if (pendingTransactionParams) {
            this.rootStore.uiStore.setCloseDialog();

            this.errorListeners.forEach((listener) =>
              listener({
                paymentMethod: EnumPaymentMethods.card,
                errorCode,
                errorDetails: details,
                pendingTransactionParams,
                transactionId: data.transactionId,
              }),
            );

            return;
          }

          const errorDetails = this.getPaysafeErrorDetails(data, providerError);

          this.rootStore.uiStore.setOpenDialog({
            dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
            options: { type: 'purchase', failureDetails: errorDetails },
          });
        }
      } else {
        this.rootStore.uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
          options: {
            transaction: data,
            type: 'purchase',
          },
        });
      }
    }
  }

  @action
  public setProcessRedeem(isProcess: boolean) {
    this.processRedeem = isProcess;
  }

  //TODO: need test for nuvei pay for redeem for bank account
  public async initRedeem(data: IInitRedeem) {
    data = { ...data, withdrawBalanceType: this.toApiWithdrawBalanceType(data.withdrawBalanceType) };
    const response = await postRequest('user/init-pay', data);
    const paymentData = get(response, 'data.data.paymentData', null);
    return paymentData;
  }

  public setInitialState() {
    this.setProcessRedeem(false);
    this.setIsRedeem(false);
    this.setConfirmRedeemSafeChargeData(null);
    this.setRedeemSafeChargeSuccessTransactionId(null);
    this.setUpo({ card: undefined, bankAccount: undefined });
    this.setRedeemData(null);
    this.setIsRedeemKYCFailed(false);
    this.setRedeemFailure({ isFailed: false });
    this.setRedemptionCaptureResult(null);
    this.setHasPaysafecardPurchaseHistoryForRedeem(false);
  }

  @action
  public setAmountRedeem(amount: number) {
    this.amountRedeem = amount;
  }

  @action
  public setIsRedeemSuccess(data: boolean) {
    this.isRedeemSuccess = data;
  }

  @action
  public setRedeemFailure(data: IRedeemFailure) {
    this.redeemFailure = data;
  }

  @action
  public setIsRedeemKYCFailed(data: boolean) {
    this.isRedeemKYCFailed = data;
  }

  @action
  public setRedeemData(data: IRedeemData | null) {
    this.redeemData = data;
  }

  @action
  public updateRedeemData(data: Partial<IRedeemData>) {
    if (!this.redeemData) {
      throw new Error('Redemption process not started yet');
    }

    this.redeemData = { ...this.redeemData, ...data };
  }

  private async performGenericRdmRequest(otp?: string): Promise<InitGenericRedemptionResult> {
    if (!this.redeemData) {
      return { status: 'error', payload: { errorMessage: 'No redemption data' } };
    }

    const method = RDM_METHOD_BY_STORED_METHOD[this.redeemData.paymentMethodType];

    if (method === NOT_SUPPORTED) {
      return {
        status: 'error',
        payload: { errorMessage: `${this.redeemData.paymentMethodType} is not a supported redemption method` },
      };
    }

    try {
      const initResponse = await postRequest<{ data: InitGenericRedemptionResult }>(
        '/v2/payment/init-generic-redemption',
        {
          amount: this.redeemCoins,
          paymentMethod: method,
          platform: 'WEB',
          paymentOptionId: this.redeemData.paymentOptionId,
          methodSpecificData: this.redeemData.methodSpecificData,
          withdrawBalanceType: this.toApiWithdrawBalanceType(this.withdrawBalanceType),
          otp,
        },
      );

      return initResponse.data;
    } catch (error) {
      return {
        status: 'error',
        payload: { errorMessage: get(error, 'response.data.errors[0].description') || 'Something went wrong' },
      };
    }
  }

  public async handleGenericRedemption(otp?: string): Promise<InitGenericRedemptionResult> {
    const redemptionAmount = this.redeemCoins;
    const redemption = await this.performGenericRdmRequest(otp);
    this.handleGenericRedemptionResponse({ redemption, redemptionAmount });
    return redemption;
  }

  public handleRedeemAppGenericRedemption({
    redemption,
    redemptionAmount,
  }: {
    redemption: InitGenericRedemptionResult;
    redemptionAmount: number;
  }) {
    try {
      this.handleGenericRedemptionResponse({ redemption, redemptionAmount });
    } catch (e) {
      console.error('Error handling generic redemption response', e);
    }
  }

  private handleGenericRedemptionResponse({
    redemption,
    redemptionAmount,
  }: {
    redemption: InitGenericRedemptionResult;
    redemptionAmount: number;
  }) {
    switch (redemption.status) {
      case 'error':
        this.setRedeemFailure({ isFailed: true, options: { errorTitle: redemption.payload.errorMessage } });
        break;
      case 'success':
      case 'pendingAuth':
        this.setRedeemSafeChargeSuccessTransactionId(redemption.payload.bankingTxnId);
        this.setRedemptionCaptureResult({
          redemptionRequestId: redemption.payload.redemptionReqId,
          redemptionAmount,
          preparationResult: {
            status: 'success',
            payload: { scheduledForAutomation: redemption.status === 'success' },
          },
        });
        void this.rootStore.walletStore.fetchWallet();
        break;
      case 'failedToSendOtp':
      case 'invalidOtp':
      case 'otpRequired':
        break;
      case 'doesNotFitLimits':
      case 'noFittingProvider':
      case 'noKyc':
      case 'notEnoughWsc':
      case 'tooManyOtpAttempts':
        this.setRedeemFailure({
          isFailed: true,
          options:
            redemption.status === 'doesNotFitLimits'
              ? { errorTitle: formatRedemptionLimitMessage(redemption.payload) }
              : undefined,
        });
        break;
      case 'pocIssue':
        this.setRedeemSafeChargeSuccessTransactionId(redemption.payload.redemptionDesc.bankingTxnId);
        this.setRedemptionCaptureResult({
          redemptionRequestId: redemption.payload.redemptionDesc.redemptionReqId,
          redemptionAmount,
          preparationResult: {
            status: 'validityIssue',
            payload: redemption.payload.pocSituation,
          },
        });
        void this.rootStore.walletStore.fetchWallet();
        break;
    }
  }

  @action
  public async handleConfirmRedeem(data: IHandleConfirmRedeemData): Promise<IConfirmRedeemResponse> {
    const { email, paymentHandleCard, paymentIdBankAccount, otp } = data;

    switch (this.redeemData?.paymentMethodType) {
      case TSafeChargeMethodType.skrill: {
        if (!email) {
          return { transactionId: -1, status: 'failed' };
        }

        const response = await this.confirmRedeemSkrill({ email: String(email), otp });
        return response;
      }
      case TSafeChargeMethodType.card: {
        const response = await this.confirmRedeemSafeCharge({
          paymentMethodType: TSafeChargeMethodType.card,
          userPMId: Number(paymentHandleCard),
          amount: this.redeemCoins,
          paymentType: TSafeChargePaymentType.redeem,
          ipAddress: '',
          withdrawBalanceType: this.toApiWithdrawBalanceType(this.withdrawBalanceType),
        });

        return response;
      }
      case TSafeChargeMethodType.payouts: {
        const response = await this.confirmPayoutsRedeem({
          paymentMethod: TSafeChargeMethodType.payouts,
          coins: this.redeemCoins,
          paymentType: TSafeChargePaymentType.redeem,
          otp,
          withdrawBalanceType: this.toApiWithdrawBalanceType(this.withdrawBalanceType),
        });

        return response;
      }
      case TSafeChargeMethodType.bank:
      case TSafeChargeMethodType.bankPaysafe: {
        return this.confirmRedeemIBT({
          coins: this.redeemCoins,
          paymentMethodType: this.redeemData?.paymentMethodType,
          paymentIdBankAccount: paymentIdBankAccount,
          ...(this.redeemData?.personalDetails ? { personalDetails: this.redeemData.personalDetails } : {}),
          otp,
          withdrawBalanceType: this.toApiWithdrawBalanceType(this.withdrawBalanceType),
        });
      }
      case TSafeChargeMethodType.interac: {
        if (!email) {
          return { transactionId: -1, status: 'failed' };
        }

        return this.confirmRedeemIBT({
          coins: this.redeemCoins,
          paymentMethodType: this.redeemData?.paymentMethodType,
          paymentIdBankAccount: email,
          otp,
          withdrawBalanceType: this.toApiWithdrawBalanceType(this.withdrawBalanceType),
        });
      }
      case TSafeChargeMethodType.paysafeCard: {
        const response = await this.confirmRedeemPaysafeCard({
          otp,
          withdrawBalanceType: this.toApiWithdrawBalanceType(this.withdrawBalanceType),
        });
        return response;
      }

      default:
        return { transactionId: -1, status: 'failed' };
    }
  }

  public async fetchRedeemTransactions({
    getNonAcknowledged = false,
    coinType = 'all',
  }: {
    getNonAcknowledged?: boolean;
    coinType?: WithdrawBalanceType | 'all';
  } = {}) {
    try {
      this.setPaymentProcess(true);

      // Map CROWN_TICKETS → 'CASH' for the server query, but keep the original coinType as the client
      // bucket key so wsc history lands in (and is read from) its own CROWN_TICKETS bucket.
      const apiCoinType = coinType === 'all' ? 'all' : this.toApiWithdrawBalanceType(coinType);
      const response = await getRequest('user/redeem-requests', {
        ...(getNonAcknowledged ? { getNonAcknowledged: true } : {}),
        coinType: apiCoinType || 'all',
      });

      if (response.data.success) {
        const { rows }: { rows: IRedeemTransaction[] } = response.data.data;
        this.setRedeemTransactions(rows, coinType || 'all');
      }
    } catch (error) {
      devError('redeem-requests: ', error);
    } finally {
      this.setPaymentProcess(false);
    }
  }

  private async subscribeToRedeemTransaction(event: any) {
    const data = get(event, 'data', {});
    if (isEmpty(data.status)) {
      return;
    }
    if (data.transactionId) {
      const eventTransactionId = String(data.transactionId);
      const prevSC = this.redeemTransactions.SC.find((item) => {
        return String(item.withdrawRequestId) === eventTransactionId;
      });
      const prevCASH = this.redeemTransactions.CASH.find((item) => {
        return String(item.withdrawRequestId) === eventTransactionId;
      });
      const prevCrownTickets = this.redeemTransactions.CROWN_TICKETS.find((item) => {
        return String(item.withdrawRequestId) === eventTransactionId;
      });
      const prev = prevSC || prevCASH || prevCrownTickets;
      const transactionCoinType = prevSC ? 'SC' : prevCASH ? 'CASH' : prevCrownTickets ? 'CROWN_TICKETS' : null;

      if (!prev) {
        await this.fetchRedeemTransactions();
        return;
      }
      if (!transactionCoinType) {
        return;
      }
      const updatedTransactions = this.redeemTransactions[transactionCoinType].map((item) => {
        if (String(item.withdrawRequestId) !== String(data.transactionId)) {
          return item;
        }
        switch (data.status) {
          case 'approved':
            return {
              ...item,
              status: RedeemTransactionStatus.APPROVED,
              acknowledged: false,
            };
          case 'rejected':
            return {
              ...item,
              status: RedeemTransactionStatus.REJECTED,
            };
          case 'activation':
            return {
              ...item,
              status: RedeemTransactionStatus.ACTIVATION,
            };
          case 'expired':
            return {
              ...item,
              status: RedeemTransactionStatus.EXPIRED,
            };
          default:
            return item;
        }
      });

      this.setRedeemTransactions(updatedTransactions, transactionCoinType);
    }
  }

  private addNotificationPopupToLobbySequenceQueue() {
    if (!resolveFlag('redemption_approval_notification', false)) {
      return;
    }

    const isInBingoArea = this.rootStore.bingoStore.isInsideBingoArea;

    if (isInBingoArea) {
      this.startDialogs();
    } else {
      this.rootStore.featureManagerStore.addDialogsToSequenceQueue(['RedemptionApproval']);
    }
  }

  @action
  public setRedeemTransactions(rawData: IRedeemTransaction[], coinType: WithdrawBalanceType | 'all') {
    const currentlyAcknowledged = new Map(
      Object.values(this.redeemTransactions).reduce<[number, boolean | undefined][]>((mapSrc, listForCoinType) => {
        mapSrc.push(
          ...listForCoinType.map<[number, boolean | undefined]>((item) => [item.withdrawRequestId, item.acknowledged]),
        );

        return mapSrc;
      }, []),
    );

    if (coinType === 'all') {
      const { data, scTransactions, cashTransactions } = rawData.reduce<{
        data: IRedeemTransaction[];
        scTransactions: IRedeemTransaction[];
        cashTransactions: IRedeemTransaction[];
      }>(
        (digest, item) => {
          const acknowledged = currentlyAcknowledged.get(item.withdrawRequestId) || item.acknowledged;
          const fixedItem = { ...item, acknowledged };
          digest.data.push(fixedItem);

          switch (item.coinType) {
            case 'SC':
              digest.scTransactions.push(fixedItem);
              break;
            case 'CASH':
              digest.cashTransactions.push(fixedItem);
              break;
          }

          return digest;
        },
        {
          data: [],
          scTransactions: [],
          cashTransactions: [],
        },
      );

      this.redeemTransactions = {
        ...this.redeemTransactions,
        SC: scTransactions,
        CASH: cashTransactions,
        // wsc (CROWN_TICKETS) redeems cash, so its history is the cash rows. Mirror them here so a bare
        // 'all' fetch hydrates the wsc bucket too (it's only ever read in cash-winnings mode).
        CROWN_TICKETS: cashTransactions,
      };

      this.onHistoryChangesReaction(data);
    } else {
      const data = rawData.map((item) => ({
        ...item,
        acknowledged: currentlyAcknowledged.get(item.withdrawRequestId) || item.acknowledged,
      }));

      this.redeemTransactions = {
        ...this.redeemTransactions,
        [coinType]: data,
      };
      this.onHistoryChangesReaction(data);
    }
  }

  @observable
  public fetchingRedeemTransactions = false;
  @action
  private setFetchingRedeemTransactions(state: boolean) {
    this.fetchingRedeemTransactions = state;
  }

  public fetch = singleFlight(async (): Promise<FeatureFetchStatus> => {
    try {
      this.setFetchingRedeemTransactions(true);
      await this.fetchRedeemTransactions({ getNonAcknowledged: true });
      return 'READY';
    } catch (error) {
      devError('fetch redemption history: ', error);
      return 'ERROR';
    } finally {
      this.setFetchingRedeemTransactions(false);
    }
  });

  private onHistoryChangesReaction(history: IRedeemTransaction[]) {
    for (const item of history) {
      if (
        [
          RedeemTransactionStatus.APPROVED,
          RedeemTransactionStatus.ACTIVATION,
          RedeemTransactionStatus.EXPIRED,
        ].includes(item.status) &&
        !item.acknowledged
      ) {
        this.addNotificationPopupToLobbySequenceQueue();
        break;
      }
    }
  }

  public dialogTypes = ['RedemptionApproval'] as const;
  public startDialogs() {
    const isOpened = this.nextRedemptionApprovalDialog();
    return isOpened ? true : false;
  }
  private nextRedemptionApprovalDialog() {
    for (const item of this.redeemTransactions[this.withdrawBalanceType]) {
      if (
        item.status === RedeemTransactionStatus.APPROVED &&
        !item.acknowledged &&
        item.paymentProvider !== 'PAYOUTS'
      ) {
        this.openRedemptionApprovalDialog(item);
        return true;
      } else if (item.status === RedeemTransactionStatus.ACTIVATION && !item.acknowledged) {
        this.openRedemptionActivationDialog(item);
        return true;
      } else if (item.status === RedeemTransactionStatus.EXPIRED && !item.acknowledged) {
        this.openExpiredRedemptionDialog(item);
        return true;
      }
    }
    return false;
  }
  private openRedemptionApprovalDialog(transaction: IRedeemTransaction) {
    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenRedemptionApprovalDialog,
      options: { transaction },
    });
  }

  private openRedemptionActivationDialog(transaction: IRedeemTransaction) {
    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenRedemptionActivationDialog,
      options: { transaction },
    });
  }

  private openExpiredRedemptionDialog(transaction: IRedeemTransaction) {
    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenRedemptionExpiredDialog,
      options: { transaction },
    });
  }

  public async closeRedemptionApprovalDialog(transaction: IRedeemTransaction) {
    this.rootStore.uiStore.setCloseDialog();
    try {
      const coinType = this.withdrawBalanceType;
      this.setRedeemTransactions(
        this.redeemTransactions[coinType].map((item) => {
          if (item.withdrawRequestId === transaction.withdrawRequestId) {
            return { ...item, acknowledged: true };
          }
          return item;
        }),
        coinType,
      );

      await postRequest(`redemptions/approval-acknowledge`, {
        withdrawRequestId: String(transaction.withdrawRequestId),
      });
    } catch (error) {
      devError('approval-acknowledge api error: ', error);
    } finally {
      this.nextRedemptionApprovalDialog();
    }
  }

  @action
  public async cancelRedeemTransaction(withdrawRequestId: number) {
    try {
      this.setRedeemTransactionProcess({ withdrawRequestId, isProcess: true });
      const response = await patchRequest(`user/redeem-requests/cancel/${withdrawRequestId}`);

      if (response.data.success) {
        void (await this.rootStore.walletStore.fetchWallet());
        if (resolveFlag('redeem_new_design_with_cash', false)) {
          // The canceled item may live in any bucket (SC/CASH/CROWN_TICKETS) — update them all.
          (Object.keys(this.redeemTransactions) as WithdrawBalanceType[]).forEach((bucket) => {
            const transactions = this.redeemTransactions[bucket];
            if (!transactions.some((item) => item.withdrawRequestId == withdrawRequestId)) {
              return;
            }
            this.setRedeemTransactions(
              transactions.map((item) =>
                item.withdrawRequestId == withdrawRequestId
                  ? { ...item, status: RedeemTransactionStatus.CANCELED }
                  : item,
              ),
              bucket,
            );
          });
        } else {
          const newRedeemTransactions = this.redeemTransactions[this.redeemTransactionsCoinType].map((item) => {
            if (item.withdrawRequestId == withdrawRequestId) {
              return {
                ...item,
                status: RedeemTransactionStatus.CANCELED,
              };
            }

            return item;
          });

          this.setRedeemTransactions(newRedeemTransactions, this.redeemTransactionsCoinType);

          // Cash-winnings lists SC + cash together, but the optimistic update above only touches the current
          // bucket. Re-fetch both ('all') so canceling a pending SC item from the merged list isn't left stale.
          const cashWinningsMode = !(this.rootStore.userStore.profile?.geoRestrictionMetadata?.isWscAvailable ?? true);
          if (cashWinningsMode) {
            await this.fetchRedeemTransactions({ coinType: 'all' });
          }
        }
      }
    } catch (error) {
      devError('cancelRedeemTransaction Error', error);
    } finally {
      this.setRedeemTransactionProcess({ withdrawRequestId, isProcess: false });
    }
  }

  @action
  public setRedeemTransactionProcess({
    withdrawRequestId,
    isProcess,
  }: {
    withdrawRequestId: number;
    isProcess: boolean;
  }) {
    const newRedeemTransactions = this.redeemTransactions[this.redeemTransactionsCoinType].map((item) => {
      if (withdrawRequestId === item.withdrawRequestId) return { ...item, isProcess };

      return item;
    });
    this.setRedeemTransactions(newRedeemTransactions, this.redeemTransactionsCoinType);
  }

  @action
  public setNewCardBillingDetails(address: INewCardBillingDetails | null) {
    this.newCardBillingDetails = address;
  }

  @action
  private setCheckForceKYC(data: Partial<ICheckForceKYC>) {
    this.checkForceKYC = { ...this.checkForceKYC, ...data };
  }

  @action
  public async handleCheckForceKYC(isSubscription = false) {
    try {
      this.setCheckForceKYC({ isProcessForKYC: true });
      const response = await getRequest('user/force-kyc');

      if (response.data) {
        const { shouldShowKYC = false } = response.data as unknown as { shouldShowKYC: boolean };

        this.setCheckForceKYC({ isForceKYC: shouldShowKYC, isProcessForKYC: false, isSubscription });
      } else {
        this.setCheckForceKYC({ isProcessForKYC: false });
      }
    } catch (e: unknown) {
      this.setCheckForceKYC(InitCheckForceKYC);
    }
  }

  @action
  private convertTimeToLiveSecondsToDate(previousPaymentData: IPreviousPaymentData) {
    return {
      ...previousPaymentData,
      paymentHandles: previousPaymentData.paymentHandles.map((savedCreditCardData) => {
        if (!savedCreditCardData.card) return savedCreditCardData;
        return { ...savedCreditCardData, timeToLiveDate: Date.now() + savedCreditCardData.timeToLiveSeconds * 1000 };
      }),
    };
  }

  @action
  public awaitForTransaction() {
    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenTransactionWaitingDialog,
    });
  }

  @observable
  public ucvStatus: UcvStatus = 'UNKNOWN';

  @observable
  public isFetchingUcvStatus: boolean = false;

  @action
  private setUcvStatus(ucvStatus: UcvStatus) {
    this.ucvStatus = ucvStatus;
  }

  @action
  private setIsFetchingUcvStatus(isFetching: boolean) {
    this.isFetchingUcvStatus = isFetching;
  }

  public async getUcvStatus() {
    this.setIsFetchingUcvStatus(true);

    try {
      const response = await getRequest<{ data: UcvStatus }>('payment/ucv-status');

      this.setUcvStatus(response.data);
    } finally {
      this.setIsFetchingUcvStatus(false);
    }
  }

  public handleSuccessPurchase(params?: { successDialogType?: 'v2' | 'v1' }) {
    const { appStore, userStore, uiStore, payRedeemStore } = this.rootStore;
    const transaction = this.transactionsEventsRegistry.get(this.inProcessTransactionId);

    if (transaction && transaction.isHandled) {
      return;
    }

    if (transaction) {
      this.transactionsEventsRegistry.set(this.inProcessTransactionId, {
        ...transaction,
        isHandled: true,
      });
    }

    // Past the isHandled guard, so a replayed transaction event doesn't re-signal. Covers both
    // branches below — providers that cache their balance need this even when the success dialog
    // (and with it updateBalanceInGame) is skipped.
    this.rootStore.gameStore.refreshSlotBalance();

    // Opening the success dialog would evict an open game-ui game (single-slot dialog registry) and
    // dump the player to the lobby — e.g. the Crown Rips "+" deposit. Keep the game up instead; its
    // balance ticks up off the wallet socket. afterPurchaseFlowInProgress is only reset by the success
    // dialog, so it must not be set when the dialog is skipped.
    const isGameUiGameOpen = uiStore.openDialog.dialog === EnumOpenDialog.isOpenGameUiDialog;

    if (!isGameUiGameOpen) {
      payRedeemStore.setAfterPurchaseFlowInProgress(true);

      uiStore.setOpenDialog({
        dialog:
          params?.successDialogType === 'v2'
            ? EnumOpenDialog.isOpenPurchaseSuccessDialogV2
            : EnumOpenDialog.isOpenPurchaseSuccessDialog,
      });
    } else {
      this.bumpGameUiPurchaseSuccess();
    }

    const selectedPackage = transaction?.selectedPackage ?? appStore.packageForPurchaseOutcome;

    if (!selectedPackage) {
      logToNewRelic(
        'sendSuccessPurchaseEvent',
        {
          message: 'No selectedPackage, not sending event',
        },
        'info',
      );
      return;
    }

    // Safe while a game-ui game is open: this only enqueues — the feature-manager sequencer holds the
    // queue until no dialog is open, so purchase-triggered dialogs fire after the game closes.
    setTimeout(() => {
      void this.rootStore.featureManagerStore.fetchAndStartDialogs(this.rootStore.eventTriggerStore.dialogTypes);
    }, 1000); // wait as a percaution for the purchase event to run first on the event service

    if (transaction && transaction.transactionId) {
      const { transactionId, isFirstDeposit, paymentMethod } = transaction;
      const { firstName, lastName, email, phone, city, zipCode, address, cleanGeoData } = userStore.profile!;
      const purchaseSource = sessionStorage.getItem('purchaseSource') as PurchaseSource;
      const isPvpPurchase = !!getUserStats(userStore.profile?.stats)?.pixel_key_optimization;

      analytics.sendSuccessPurchaseEvent({
        currency: userStore.profile?.currencyCode ?? 'USD',
        // Prefer the charged amount reported by the payments app — a cash deposit has no real package
        // (its placeholder carries price 0), and for package purchases the two values match.
        amount: transaction.amount ?? selectedPackage.price ?? 0,
        packageId: selectedPackage.packageId,
        ftd: isFirstDeposit ?? false,
        source: purchaseSource,
        store_key: appStore.currentStore?.key || '',
        sign_up_date: userStore.profile?.createdAt || '',
        isPvpPurchase,
        eventParams: {
          transaction_id: transactionId,
          payment_method: paymentMethod ?? this.paymentMethod,
          social_cc_package: selectedPackage.gcAmount,
          coins_usc_package: selectedPackage.scAmount,
          store_segment: this.rootStore.loyaltyProgramStore.userLoyalty?.currentTier?.key ?? 'entry',
        },
        gtagParams: {
          transaction_id: transactionId,
          firstname: firstName,
          lastname: lastName,
          email,
          phonenumber: phone,
          city,
          state: cleanGeoData?.territoryCode,
          street: address,
          postalcode: zipCode,
          country: cleanGeoData?.countryCode,
          item: selectedPackage.packageId,
        },
      });

      appStore.cleanCurrentOrderPackage();
    } else {
      logToNewRelic(
        'sendSuccessPurchaseEvent',
        {
          message: 'No transactionId, not sending event',
          data: transaction,
        },
        'info',
      );
    }
  }
}
